import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import './ImageModal.css';

/**
 * ImageModal - Shared full screen image viewer with horizontal scroll navigation
 * Issue #119: Support horizontal scrolling between multiple images with smooth sliding
 * Issue #119 accessibility: Added focus trap, navigation buttons, dot indicators, live region
 * Issue #163: Infinite/continuous scrolling - last image connects smoothly to first
 *
 * @param {string} imageUrl - Single image URL (backwards compatible)
 * @param {string[]} images - Array of image URLs for gallery mode
 * @param {number} initialIndex - Starting index for gallery mode
 * @param {function} onClose - Callback when modal is closed
 */
const ImageModal = ({ imageUrl, images, initialIndex = 0, onClose }) => {
  const { t } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const touchStartX = useRef(null);
  const containerRef = useRef(null);
  const modalRef = useRef(null);
  const previouslyFocusedElement = useRef(null);
  const [announcement, setAnnouncement] = useState('');

  // Determine image array - support both single imageUrl and images array
  const imageArray = images || (imageUrl ? [imageUrl] : []);
  const hasMultipleImages = imageArray.length > 1;

  // Issue #163: For infinite scroll, we add clones: [last, ...original, first]
  // So the slider position is offset by 1 to account for the cloned last image at the start
  // For single images, no offset needed
  const [sliderIndex, setSliderIndex] = useState(
    hasMultipleImages ? initialIndex + 1 : initialIndex
  );

  // Reset index when images change
  useEffect(() => {
    setCurrentIndex(initialIndex);
    setSliderIndex(hasMultipleImages ? initialIndex + 1 : initialIndex);
  }, [initialIndex, images, hasMultipleImages]);

  // Issue #163: Handle the snap-back after transitioning to a clone
  useEffect(() => {
    if (!isTransitioning || !hasMultipleImages) return;

    const handleTransitionEnd = () => {
      setIsTransitioning(false);
      // If we're at a clone position, instantly jump to the real position
      if (sliderIndex === 0) {
        // We're at the clone of the last image (at start), jump to real last
        setSliderIndex(imageArray.length);
      } else if (sliderIndex === imageArray.length + 1) {
        // We're at the clone of the first image (at end), jump to real first
        setSliderIndex(1);
      }
    };

    const timer = setTimeout(handleTransitionEnd, 300); // Match CSS transition duration
    return () => clearTimeout(timer);
  }, [isTransitioning, sliderIndex, imageArray.length, hasMultipleImages]);

  // Issue #119 accessibility: Focus trap and focus management
  useEffect(() => {
    // Store previously focused element to restore on close
    previouslyFocusedElement.current = document.activeElement;

    // Lock body scroll to prevent scrollbar
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Issue #393: Mark body so shared overlay UI (e.g. FloatingPlusButton)
    // can hide itself while the photo gallery is open.
    document.body.classList.add('image-modal-open');

    // Focus the modal overlay when it opens (allows Escape key to work)
    if (modalRef.current) {
      modalRef.current.focus();
    }

    // Restore focus and body scroll when modal closes
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.classList.remove('image-modal-open');
      if (previouslyFocusedElement.current) {
        previouslyFocusedElement.current.focus();
      }
    };
  }, []);

  // Issue #119 accessibility: Focus trap - keep focus within modal
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Tab') {
      const focusableElements = modalRef.current?.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );

      if (!focusableElements || focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  }, []);

  // Issue #163: Continuous scroll navigation - go to previous image
  const goToPrevious = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setSliderIndex((prev) => prev - 1);
    setCurrentIndex((prev) => {
      const newIndex = prev === 0 ? imageArray.length - 1 : prev - 1;
      // Issue #119 accessibility: Announce image change to screen readers
      setAnnouncement(t('imageModal.imageAnnouncement', { current: newIndex + 1, total: imageArray.length }));
      return newIndex;
    });
  }, [imageArray.length, t, isTransitioning]);

  // Issue #163: Continuous scroll navigation - go to next image
  const goToNext = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setSliderIndex((prev) => prev + 1);
    setCurrentIndex((prev) => {
      const newIndex = prev === imageArray.length - 1 ? 0 : prev + 1;
      // Issue #119 accessibility: Announce image change to screen readers
      setAnnouncement(t('imageModal.imageAnnouncement', { current: newIndex + 1, total: imageArray.length }));
      return newIndex;
    });
  }, [imageArray.length, t, isTransitioning]);

  // goToIndex removed - dot indicators no longer used

  // Handle keyboard navigation (Escape and Arrow keys)
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && hasMultipleImages) {
        goToPrevious();
      } else if (e.key === 'ArrowRight' && hasMultipleImages) {
        goToNext();
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [hasMultipleImages, onClose, goToPrevious, goToNext]);

  // Touch handling for swipe gestures with smooth sliding
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    touchStartX.current = e.targetTouches[0].clientX;
    setIsDragging(true);
  };

  const onTouchMove = (e) => {
    if (touchStartX.current === null) return;

    const currentX = e.targetTouches[0].clientX;
    const diff = currentX - touchStartX.current;

    // Issue #163: Allow full drag since we wrap around
    setDragOffset(diff);
  };

  // Issue #163: Updated to allow wrap-around swipe navigation
  const onTouchEnd = () => {
    setIsDragging(false);

    if (Math.abs(dragOffset) > minSwipeDistance) {
      if (dragOffset < 0) {
        goToNext();
      } else if (dragOffset > 0) {
        goToPrevious();
      }
    }

    setDragOffset(0);
    touchStartX.current = null;
  };

  const handleOverlayClick = (e) => {
    // Only close if clicking the overlay, not the image or navigation
    if (e.target.classList.contains('image-modal-overlay')) {
      onClose();
    }
  };

  if (imageArray.length === 0) return null;

  // Issue #163: Calculate the transform for sliding effect with infinite scroll
  const getSliderStyle = () => {
    // Use sliderIndex which accounts for clone images
    const baseOffset = -sliderIndex * 100;
    const dragPercent = containerRef.current
      ? (dragOffset / containerRef.current.offsetWidth) * 100
      : 0;

    // Disable transition when snapping back from clone to real position
    const shouldAnimate = isDragging ? false : isTransitioning;

    return {
      transform: `translateX(${baseOffset + dragPercent}%)`,
      transition: shouldAnimate ? 'transform 0.3s ease-out' : 'none',
    };
  };

  // Issue #163: Build the extended image array with clones for infinite scroll
  // Structure: [lastImage, ...allImages, firstImage]
  const getExtendedImages = () => {
    if (!hasMultipleImages) return imageArray;
    return [
      imageArray[imageArray.length - 1], // Clone of last at start
      ...imageArray,
      imageArray[0], // Clone of first at end
    ];
  };

  const extendedImages = getExtendedImages();

  return (
    <div
      ref={modalRef}
      className="image-modal-overlay"
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-label={t('imageModal.dialogLabel', { current: currentIndex + 1, total: imageArray.length })}
      tabIndex={-1}
    >
      {/* Issue #119 accessibility: Live region for screen reader announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {announcement}
      </div>

      {/* Navigation: Use Arrow keys or swipe gestures to navigate between images */}
      {/* Close: Click overlay or press Escape */}

      {/* Image slider container */}
      <div
        ref={containerRef}
        className="image-modal-content"
        onTouchStart={hasMultipleImages ? onTouchStart : undefined}
        onTouchMove={hasMultipleImages ? onTouchMove : undefined}
        onTouchEnd={hasMultipleImages ? onTouchEnd : undefined}
      >
        <div className="image-modal-slider" style={getSliderStyle()}>
          {extendedImages.map((src, index) => {
            // Issue #163: Calculate the real image index for alt text
            // extendedImages = [clone-last, img0, img1, ..., imgN, clone-first]
            let realIndex;
            if (!hasMultipleImages) {
              realIndex = index;
            } else if (index === 0) {
              realIndex = imageArray.length - 1; // Clone of last
            } else if (index === extendedImages.length - 1) {
              realIndex = 0; // Clone of first
            } else {
              realIndex = index - 1; // Real images are offset by 1
            }

            return (
              <div key={index} className="image-modal-slide">
                <img
                  src={src}
                  alt={t('imageModal.imageAlt', { current: realIndex + 1, total: imageArray.length })}
                  className="image-modal-img"
                  draggable="false"
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ImageModal;
